import { Rng } from '../core/rng';
import { clamp, Vec3 } from '../core/math';
import { CANNONS, DECK, MAIN_DECK_OBSTACLES, cannonMount, deckHalfWidth, deckY, sheerY, type DeckObstacle } from '../data/ship';
import type { SimEvents } from './events';

// Things on deck that respond to the ship's motion: loose barrels and crates
// sliding on the main deck, and hanging lanterns swinging on their hooks.
// Everything here lives in the ship-local frame.

export interface DeckObject {
  kind: 'barrel' | 'crate';
  x: number;
  z: number;
  vx: number;
  vz: number;
  yaw: number;
  yawVel: number;
  r: number;
  h: number;
  mass: number;
  /** Lashed down objects never move. */
  loose: boolean;
  /** Visual rocking (radians) from sliding / acceleration. */
  tiltX: number;
  tiltZ: number;
}

export interface Lantern {
  /** Fixed lanterns have pivot === bob. */
  hanging: boolean;
  pivot: Vec3;
  bob: Vec3;
  prev: Vec3;
  length: number;
  /** Flicker phase seed. */
  seed: number;
}

const X_MIN = DECK.quarterFrontX + 0.2;
const X_MAX = DECK.foreBackX - 0.2;

export class DeckCargo {
  readonly objects: DeckObject[] = [];
  readonly lanterns: Lantern[] = [];
  private readonly obstacles: DeckObstacle[];

  constructor(rng: Rng) {
    // Gun carriages on the main deck are obstacles too.
    const guns: DeckObstacle[] = CANNONS.filter((c) => c.deck === 'main').map((c) => {
      const m = cannonMount(c);
      return { kind: 'box', x: c.x, z: m.z, hx: 0.62, hz: 1.05 };
    });
    this.obstacles = [...MAIN_DECK_OBSTACLES, ...guns];

    const place = (kind: DeckObject['kind'], x: number, z: number, loose: boolean) => {
      const barrel = kind === 'barrel';
      this.objects.push({
        kind, x, z, vx: 0, vz: 0, yaw: rng.range(0, Math.PI * 2), yawVel: 0,
        r: barrel ? 0.36 : 0.42, h: barrel ? 0.95 : 0.7, mass: barrel ? 140 : 90, loose, tiltX: 0, tiltZ: 0,
      });
    };
    // Lashed stores by the mainmast and forward.
    for (const [x, z] of [[2.3, -0.9], [2.3, -0.15], [2.3, 0.6], [2.95, -0.55], [2.95, 0.25]] as const) place('barrel', x, z, false);
    for (const [x, z] of [[10.9, -1.2], [10.9, 1.2]] as const) place('crate', x, z, false);
    // Loose gear that will slide when she rolls.
    place('barrel', -1.2, 1.4, true);
    place('barrel', -0.6, -1.5, true);
    place('barrel', 7.6, 1.1, true);
    place('crate', 7.4, -1.3, true);
    place('crate', -1.6, -0.6, true);
    place('barrel', 3.0, 2.2, true);

    const addFixed = (x: number, y: number, z: number) => {
      const p = new Vec3(x, y, z);
      this.lanterns.push({ hanging: false, pivot: p, bob: p.clone(), prev: p.clone(), length: 0, seed: rng.next() * 100 });
    };
    const addHanging = (x: number, y: number, z: number, len: number) => {
      const p = new Vec3(x, y, z);
      const b = new Vec3(x, y - len, z);
      this.lanterns.push({ hanging: true, pivot: p, bob: b, prev: b.clone(), length: len, seed: rng.next() * 100 });
    };
    // Three great stern lanterns on the taffrail.
    const ty = sheerY(-18.3) + 1.25;
    addFixed(-18.45, ty + 0.35, 0);
    addFixed(-18.2, ty, -1.9);
    addFixed(-18.2, ty, 1.9);
    // Hanging lanterns by the cabin doors, the mainmast and the belfry.
    addHanging(DECK.quarterFrontX - 0.25, DECK.quarterY - 0.15, -1.5, 0.55);
    addHanging(DECK.quarterFrontX - 0.25, DECK.quarterY - 0.15, 1.5, 0.55);
    addHanging(1.62, 4.7, 0, 0.5);
    addHanging(12.3, deckY(12.5) + 1.9, 0.9, 0.45);
  }

  update(dt: number, g: Vec3, deckWater: number, waterSide: number, wet: number, events: SimEvents, toWorld: (p: Vec3, out: Vec3) => Vec3): void {
    const normal = Math.max(0, -g.y);
    const mu = 0.5 - 0.18 * wet;
    const muK = mu * 0.75;
    // Green water sweeping across the deck from the side it came aboard.
    const flowZ = -waterSide * deckWater * 4.5;

    for (const o of this.objects) {
      const tx = g.x;
      const tz = g.z + (o.loose ? flowZ : 0);
      o.tiltX += (clamp(tz * 0.02, -0.12, 0.12) - o.tiltX) * Math.min(1, dt * 6);
      o.tiltZ += (clamp(-tx * 0.02, -0.12, 0.12) - o.tiltZ) * Math.min(1, dt * 6);
      if (!o.loose) continue;
      const speed = Math.hypot(o.vx, o.vz);
      const tmag = Math.hypot(tx, tz);
      if (speed < 0.03 && tmag < mu * normal) {
        o.vx = 0;
        o.vz = 0;
      } else {
        let ax = tx, az = tz;
        if (speed > 1e-4) {
          ax -= (muK * normal * o.vx) / speed;
          az -= (muK * normal * o.vz) / speed;
        }
        o.vx += ax * dt;
        o.vz += az * dt;
        // Friction must not reverse the motion within a step.
        if (speed > 1e-4 && o.vx * (o.vx - ax * dt) < 0 && Math.abs(tx) < mu * normal) o.vx = 0;
        if (speed > 1e-4 && o.vz * (o.vz - az * dt) < 0 && Math.abs(tz) < mu * normal) o.vz = 0;
      }
      o.x += o.vx * dt;
      o.z += o.vz * dt;
      o.yawVel += (o.vx * 0.3 - o.yawVel) * dt;
      o.yaw += o.yawVel * dt;
    }

    // Collisions: deck edges, fittings, each other.
    for (const o of this.objects) {
      if (!o.loose) continue;
      let hit = 0;
      const hw = deckHalfWidth(o.x) - o.r - 0.05;
      if (o.z > hw) { hit = Math.max(hit, Math.abs(o.vz)); o.z = hw; o.vz *= -0.3; }
      if (o.z < -hw) { hit = Math.max(hit, Math.abs(o.vz)); o.z = -hw; o.vz *= -0.3; }
      if (o.x < X_MIN + o.r) { hit = Math.max(hit, Math.abs(o.vx)); o.x = X_MIN + o.r; o.vx *= -0.3; }
      if (o.x > X_MAX - o.r) { hit = Math.max(hit, Math.abs(o.vx)); o.x = X_MAX - o.r; o.vx *= -0.3; }
      for (const b of this.obstacles) hit = Math.max(hit, pushOut(o, b));
      if (hit > 1.1) {
        const p = toWorld(tmp.set(o.x, deckY(o.x) + 0.3, o.z), tmp);
        events.push({ type: 'objectHit', intensity: clamp(hit / 3, 0.2, 1), x: p.x, y: p.y, z: p.z });
      }
    }
    for (let i = 0; i < this.objects.length; i++) {
      for (let j = i + 1; j < this.objects.length; j++) {
        const a = this.objects[i]!, b = this.objects[j]!;
        if (!a.loose && !b.loose) continue;
        const dx = b.x - a.x, dz = b.z - a.z;
        const d = Math.hypot(dx, dz);
        const min = a.r + b.r;
        if (d >= min || d < 1e-6) continue;
        const nx = dx / d, nz = dz / d;
        const wa = a.loose ? 1 / a.mass : 0, wb = b.loose ? 1 / b.mass : 0;
        const pen = (min - d) / (wa + wb);
        a.x -= nx * pen * wa;
        a.z -= nz * pen * wa;
        b.x += nx * pen * wb;
        b.z += nz * pen * wb;
        const rv = (b.vx - a.vx) * nx + (b.vz - a.vz) * nz;
        if (rv < 0) {
          const jimp = (-(1.3) * rv) / (wa + wb);
          a.vx -= jimp * wa * nx;
          a.vz -= jimp * wa * nz;
          b.vx += jimp * wb * nx;
          b.vz += jimp * wb * nz;
          if (-rv > 1.1) {
            const p = toWorld(tmp.set((a.x + b.x) / 2, deckY(a.x) + 0.4, (a.z + b.z) / 2), tmp);
            events.push({ type: 'objectHit', intensity: clamp(-rv / 3, 0.2, 1), x: p.x, y: p.y, z: p.z });
          }
        }
      }
    }

    // Lanterns: Verlet pendulums under the effective gravity.
    const dt2 = dt * dt;
    for (const l of this.lanterns) {
      if (!l.hanging) continue;
      const b = l.bob;
      const px = b.x, py = b.y, pz = b.z;
      b.x += (b.x - l.prev.x) * 0.992 + g.x * dt2;
      b.y += (b.y - l.prev.y) * 0.992 + g.y * dt2;
      b.z += (b.z - l.prev.z) * 0.992 + g.z * dt2;
      l.prev.set(px, py, pz);
      const dx = b.x - l.pivot.x, dy = b.y - l.pivot.y, dz = b.z - l.pivot.z;
      const d = Math.hypot(dx, dy, dz) || 1;
      const s = l.length / d;
      b.set(l.pivot.x + dx * s, l.pivot.y + dy * s, l.pivot.z + dz * s);
    }
  }
}

const tmp = new Vec3();

/** Resolve overlap of a sliding object with a fixed obstacle; returns impact speed. */
function pushOut(o: DeckObject, b: DeckObstacle): number {
  if (b.kind === 'circle') {
    const dx = o.x - b.x, dz = o.z - b.z;
    const d = Math.hypot(dx, dz);
    const min = o.r + b.r!;
    if (d >= min || d < 1e-6) return 0;
    const nx = dx / d, nz = dz / d;
    o.x = b.x + nx * min;
    o.z = b.z + nz * min;
    const vn = o.vx * nx + o.vz * nz;
    if (vn < 0) {
      o.vx -= 1.3 * vn * nx;
      o.vz -= 1.3 * vn * nz;
      return -vn;
    }
    return 0;
  }
  const hx = b.hx! + o.r, hz = b.hz! + o.r;
  const dx = o.x - b.x, dz = o.z - b.z;
  if (Math.abs(dx) >= hx || Math.abs(dz) >= hz) return 0;
  const px = hx - Math.abs(dx), pz = hz - Math.abs(dz);
  if (px < pz) {
    o.x = b.x + Math.sign(dx) * hx;
    const v = Math.abs(o.vx);
    o.vx = Math.sign(dx) * Math.abs(o.vx) * 0.3;
    return v;
  }
  o.z = b.z + Math.sign(dz) * hz;
  const v = Math.abs(o.vz);
  o.vz = Math.sign(dz) * Math.abs(o.vz) * 0.3;
  return v;
}
